import type { GitRef, GitRefResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import type { DeploymentProcessLoaderData } from "~/areas/projects/components/Process/DeploymentProcessPage";
import { isDataWithInvalidGitRef } from "~/areas/projects/components/Process/DeploymentProcessPage";
import useDeploymentProcessState from "~/areas/projects/components/Process/useDeploymentProcessState";
import { VcsError } from "~/areas/projects/components/VersionControl/VcsError";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import type { SetRequiresKubernetesTarget } from "../ProjectLayout/ProjectLayout";
import ProcessStepsLayoutLoader from "./ProcessStepsLayoutLoader";
interface DeploymentProcessEditorPageQueryParams {
    new: boolean | undefined;
    stepTemplates: boolean | undefined;
    childStepTemplates: boolean | undefined;
    actionType: string | undefined;
    templateId: string | undefined;
    parentStepId: string | undefined;
    actionId: string | undefined;
    newlyCreatedProject: boolean | undefined;
    actionCategory: string | undefined;
    blueprintSlug: string | undefined;
    blueprintVersionMask: string | undefined;
    blueprintActionId: string | undefined;
    blueprintParentStepId: string | undefined;
    tags: string[] | undefined;
}
export type DeploymentProcessEditorPageProps = {
    loaderData: DeploymentProcessLoaderData;
    projectContext: ProjectContextProps;
    setShowK8sStatusItem: SetRequiresKubernetesTarget;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    queryParams: DeploymentProcessEditorPageQueryParams;
    gitRef: string | undefined;
};
export function DeploymentProcessEditorPage({ setShowK8sStatusItem, projectContext, loaderData, scrollAreaRef, queryParams, gitRef }: DeploymentProcessEditorPageProps) {
    const { model: project } = projectContext.state;
    const { deploymentProcessIdentifier, deploymentProcessData, reloadDeploymentProcess, modifyDeploymentProcess } = useDeploymentProcessState(project, loaderData, gitRef);
    const spaceNavigation = useSpaceAwareNavigation();
    const navigateToGitRef = (gitRef: string) => spaceNavigation.navigate(links.branchDeploymentProcessEditorPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }, queryParams));
    if (isDataWithInvalidGitRef(deploymentProcessData)) {
        return (<PageContent header={{ title: "Process Editor" }}>
                <VcsError project={project} error={deploymentProcessData.gitRefValidationResult} gitRef={gitRef} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    const onProcessSave = (gitRef: GitRefResource | string | undefined) => projectContext.actions.onProjectUpdated(project, gitRef);
    const refreshModel = (gitRef?: GitRef) => projectContext.actions.refreshModel(gitRef);
    return (<ProcessStepsLayoutLoader setShowK8sStatusItem={setShowK8sStatusItem} project={projectContext.state.model} gitRef={gitRef} gitRefResource={deploymentProcessData.gitRefValidationResult?.GitRef} onProcessSave={onProcessSave} changeGitRef={navigateToGitRef} refreshModel={refreshModel} processIdentifier={deploymentProcessIdentifier} process={deploymentProcessData.process} reloadProcess={reloadDeploymentProcess} modifyProcess={modifyDeploymentProcess} includedLibraryVariableSetIds={project.IncludedLibraryVariableSetIds} releaseCreationPackageStepId={project.ReleaseCreationStrategy.ReleaseCreationPackageStepId} onValidationError={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" })} titleAccessory={<PageHeaderBranchSelector project={project} gitRef={gitRef} setGitRef={navigateToGitRef}/>}/>);
}
